// ────────────────────────────────────────────────────────────────────────────
// Push notifications (Firebase Cloud Messaging).
// Registers this device's FCM token with api.alogo.io so the admin "Send push"
// page can reach it. NATIVE ONLY — a no-op on web/PWA, where the plugin (and the
// heavy `firebase` SDK) are never imported, thanks to the dynamic import behind
// the platform guard.
// ────────────────────────────────────────────────────────────────────────────
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import client from '@/lib/API/client'
import router from '@/router'
import { currentTimeZone, deviceTokenPayload } from '@/lib/push-timezone'
import { markOpened } from '@/lib/hooks/use-notifications'

let listening = false
let currentToken = null
let registrationPromise = null
let apnsTokenReady = false
let apnsRetryPending = false
let pushEnabled = false

const isMissingApnsTokenError = (error) =>
    String(error?.message || error).includes('No APNS token specified before fetching FCM Token')

async function getAppVersion() {
    try {
        const info = await CapApp.getInfo()
        return info?.version ?? null
    } catch {
        return null
    }
}

// Route a tapped notification's `url` payload: an in-app path navigates the
// router, an absolute URL opens the browser, anything else is ignored.
// Program pushes (data.type = 'program') carry /horses/{id}, or /horses when
// the morning digest covers several horses. The notifications journal opens its
// rows the same way.
export function openDeepLink(url) {
    if (!url || typeof url !== 'string') return
    if (url.startsWith('/')) {
        router.push(url).catch(() => undefined)
    } else if (/^https?:\/\//i.test(url)) {
        window.open(url, '_blank')
    }
}

function retryRegistrationAfterApns() {
    if (!apnsRetryPending || !apnsTokenReady) return registrationPromise ?? Promise.resolve()

    apnsRetryPending = false
    return startPushRegistration(true)
}

function handleApnsTokenReceived() {
    apnsTokenReady = true
    if (!pushEnabled || !apnsRetryPending) return

    // If APNs arrives while getToken() is still rejecting, retry only after the
    // active registration has released its concurrency lock.
    Promise.resolve(registrationPromise).then(() => retryRegistrationAfterApns())
}

function startPushRegistration(isApnsRetry = false) {
    if (registrationPromise) return registrationPromise

    registrationPromise = registerPushOnce(isApnsRetry).finally(() => {
        registrationPromise = null
    })

    return registrationPromise
}

async function registerPushOnce(isApnsRetry) {
    const platform = Capacitor.getPlatform()

    try {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging')
        const appVersion = await getAppVersion()

        // Permission: keep an existing grant, otherwise prompt the system dialog.
        let { receive } = await FirebaseMessaging.checkPermissions()
        if (receive !== 'granted') {
            receive = (await FirebaseMessaging.requestPermissions()).receive
        }
        if (receive !== 'granted') return // user declined — stay silent

        // Re-register on token rotation + open the deep link when a notification
        // is tapped (the broadcast "url" payload). Attach both once.
        if (!listening) {
            if (platform === 'ios') {
                await FirebaseMessaging.addListener('apnsTokenReceived', handleApnsTokenReceived)
            }
            await FirebaseMessaging.addListener('tokenReceived', (event) => {
                if (pushEnabled && event?.token) {
                    currentToken = event.token
                    sendToken(event.token, platform, appVersion)
                }
            })
            await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
                const data = event?.notification?.data
                // Subject 7: read and opened in the journal and the history.
                void markOpened(data?.notification_id)
                openDeepLink(data?.url)
            })
            listening = true
        }

        try {
            await fetchAndSendToken(FirebaseMessaging, platform, appVersion)
        } catch (error) {
            if (!pushEnabled) return

            const shouldWaitForApns = platform === 'ios' && !isApnsRetry && isMissingApnsTokenError(error)

            if (!shouldWaitForApns) throw error

            if (apnsTokenReady) {
                // APNs arrived while the first FCM request was in flight.
                await fetchAndSendToken(FirebaseMessaging, platform, appVersion)
            } else {
                // The APNs listener will trigger exactly one retry.
                apnsRetryPending = true
            }
        }
    } catch (e) {
        // Missing native config, denied permission, no network — all non-fatal.
        console.warn('push registration failed', e)
    }
}

/**
 * Ask for notification permission, grab the FCM token and register it with the
 * API. Safe to call repeatedly (every login / app resume) — concurrent calls
 * share one attempt, while later calls re-sync the current token. Never throws.
 */
export function registerPush() {
    if (Capacitor.getPlatform() === 'web') return Promise.resolve() // PWA: no native FCM

    pushEnabled = true
    if (registrationPromise) return registrationPromise
    if (!apnsRetryPending) return startPushRegistration()

    // A later foreground is also allowed to consume the single retry. This
    // prevents a broken APNs setup from suppressing the error forever.
    apnsRetryPending = false
    return startPushRegistration(true)
}

/** Deactivate this device's token server-side (e.g. on logout). Best-effort. */
export async function unregisterPush() {
    pushEnabled = false
    apnsRetryPending = false
    if (Capacitor.getPlatform() === 'web' || !currentToken) return
    try {
        // Bounded on purpose: logout awaits this call, and the shared client has
        // no default timeout. A flaky mobile network must not hang the sign-out.
        await client.delete('/device-tokens', { data: { token: currentToken }, timeout: 5000 })
        currentToken = null
    } catch {
        // non-fatal
    }
}

async function fetchAndSendToken(FirebaseMessaging, platform, appVersion) {
    const { token } = await FirebaseMessaging.getToken()
    if (!pushEnabled || !token) return

    currentToken = token
    await sendToken(token, platform, appVersion)
}

function sendToken(token, platform, appVersion) {
    return client
        .post('/device-tokens', deviceTokenPayload({ token, platform, appVersion, timeZone: currentTimeZone() }))
        .catch(() => undefined)
}
